import React from 'react';
import { Moment } from 'moment';
import { Table } from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import normalize from '@shared/utils/normalizeNumber';
import { Currencies, DACostItemTypes, LinerCostUnits } from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { toMoment } from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import { formatNumber } from '@shared/utils/formatNumber';
import { CargoProps } from '@api/models/cargo';
import { CargoPortProps } from '@api/models/cargo-port';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import { EditableColumn } from '@client/components/EditableTable/EditableTable';
import { DACostItem } from './DisbursementAccountModal';

const COST_ITEM_TYPE_ORDER: Values<typeof DACostItemTypes>[] = [
	DACostItemTypes.PORT_COST,
	DACostItemTypes.LINER_COST,
	DACostItemTypes.TC_IN_OWNER_REBILLABLE,
	DACostItemTypes.CHARTERER_COST,
];

const getCostItemTypeSortIndex = (type: Values<typeof DACostItemTypes>): number => {
	const index = COST_ITEM_TYPE_ORDER.indexOf(type);

	return index === -1 ? COST_ITEM_TYPE_ORDER.length : index;
};

const compareCostItemTypes = (a: DACostItem, b: DACostItem): number =>
	getCostItemTypeSortIndex(a.type) - getCostItemTypeSortIndex(b.type);

const compareCostItemCategories = (a: DACostItem, b: DACostItem): number =>
	(a.category ?? '').localeCompare(b.category ?? '', undefined, { sensitivity: 'base' });

const COST_ITEM_DATA_COLUMN_COUNT = 8;
const COST_ITEM_AMOUNT_COLUMN_INDEX = 7;
const COST_ITEM_ACTIONS_COLUMN_INDEX = 8;

export type CostItemTypeSubtotal = {
	type: Values<typeof DACostItemTypes>;
	amount: number;
};

type CostItemDisplayAmountArgs = {
	cargos: Array<CargoProps & { CargoPorts: Array<CargoPortProps> }>;
	selectedPortCall: ItineraryPortCallDto | undefined;
};

export const getCostItemDisplayAmount = (
	row: DACostItem,
	{ cargos, selectedPortCall }: CostItemDisplayAmountArgs,
): number => {
	if (row.type === DACostItemTypes.LINER_COST && row.cargoId) {
		if (row.unitType === LinerCostUnits.LS) {
			return normalize(row.price ?? 0);
		}

		const relevantCargo = cargos.find((c) => c.id === row.cargoId);

		if (relevantCargo && selectedPortCall) {
			const cp = relevantCargo.CargoPorts.find(
				(cpo) => cpo.portCallId === selectedPortCall.id,
			);

			if (cp) {
				let qty = cp.blQuantity ?? cp.cpQuantity ?? 0;

				if (
					row.unitType &&
					relevantCargo.secondaryUnit &&
					relevantCargo.secondaryUnit !== relevantCargo.unit &&
					row.unitType === relevantCargo.secondaryUnit
				) {
					qty =
						cp.secondaryBlQuantity ??
						cp.secondaryCpQuantity ??
						cp.blQuantity ??
						cp.cpQuantity ??
						0;				
				}

				return normalize(qty) * normalize(row.price ?? 0);
			}
		}
	}

	return normalize(row.price ?? 0) * normalize(row.units ?? 0);
};

export const getCostItemSubtotalsByType = (
	items: DACostItem[],
	displayAmountArgs: CostItemDisplayAmountArgs,
): CostItemTypeSubtotal[] => {
	const totalsByType = new Map<Values<typeof DACostItemTypes>, number>();

	items.forEach((item) => {
		const amount = getCostItemDisplayAmount(item, displayAmountArgs);
		totalsByType.set(item.type, (totalsByType.get(item.type) ?? 0) + amount);
	});

	return COST_ITEM_TYPE_ORDER
		.filter((type) => totalsByType.has(type))
		.map((type) => ({
			type,
			amount: totalsByType.get(type) ?? 0,
		}));
};

export const renderCostItemsTableSummary = (
	subtotals: CostItemTypeSubtotal[],
	currency: Values<typeof Currencies> | undefined,
) => () => (
	<>
		{subtotals.map(({ type, amount }) => (
			<Table.Summary.Row key={type}>
				<Table.Summary.Cell index={0}>
					<b>{type} subtotal</b>
				</Table.Summary.Cell>
				{Array.from({ length: COST_ITEM_DATA_COLUMN_COUNT - 2 }, (_, i) => (
					<Table.Summary.Cell key={i + 1} index={i + 1} />
				))}
				<Table.Summary.Cell index={COST_ITEM_AMOUNT_COLUMN_INDEX} align="right">
					<b>{currency ? formatCurrency(amount, currency) : '-'}</b>
				</Table.Summary.Cell>
				<Table.Summary.Cell index={COST_ITEM_ACTIONS_COLUMN_INDEX} />
			</Table.Summary.Row>
		))}
	</>
);

/** Default sort for DA Desk line items: type groups, then category A–Z within each group. */
export const sortCostItemsByDisDefault = (items: DACostItem[]): DACostItem[] =>
	[...items].sort((a, b) => {
		const typeDiff = compareCostItemTypes(a, b);

		if (typeDiff !== 0) {
			return typeDiff;
		}

		return compareCostItemCategories(a, b);
	});

export const getCostItemColumns = ({
	selectedPortCall,
	cargos,
	cargoOptions,
	costItemTypeOptions,
	categoryOptions,
	unitTypeOptions,
	currency
}: {
	unitTypeOptions: Array<{
		value: DACostItem['unitType'];
		label: DACostItem['unitType'];
	}>;
	selectedPortCall: ItineraryPortCallDto | undefined;
	cargos: Array<CargoProps & { CargoPorts: Array<CargoPortProps> }>;
	cargoOptions: {
		value: number;
		label: string;
	}[],
	costItemTypeOptions: {
		value: DACostItemTypes,
		label: DACostItemTypes
	}[],
	categoryOptions: {
		value: string,
		label: string
	}[],
	currency: Values<typeof Currencies> | undefined
}): EditableColumn<DACostItem, 'id'>[] => {
	return [
		{
			title: 'Type',
			dataIndex: 'type',
			sorter: compareCostItemTypes,
			editable: true,
			width: 250,
			editingProps: {
				type: 'select' as const,
				options: costItemTypeOptions,
				inputProps: {
					'data-testid': 'new-cost-item-type'
				},
			},
		},
		{
			width: 150,
			title: 'Category',
			dataIndex: 'category',
			sorter: compareCostItemCategories,
			editable: true,
			editingProps: {
				type: 'select' as const,
				options: categoryOptions,
				inputProps: {
					'data-testid': 'new-cost-item-category'
				},
			},
			render: (category: string) => category ?? '-',
		},
		{
			title: 'Description',
			dataIndex: 'description',
			width: 175,
			editable: true,
			editingProps: {
				type: 'string' as const,
				inputProps: {
					'data-testid': 'new-cost-item-description'
				},
			},
			render: (description: string) => description ?? '-',
		},
		{
			title: 'Cargo',
			dataIndex: 'cargoId',
			width: 175,
			editable: (row?: DACostItem) => {
				return row?.type === 'Liner cost';
			},
			editingProps: {
				type: 'select' as const,
				options: cargoOptions,
				inputProps: {
					'data-testid': 'new-cost-item-cargo'
				},
			},
			render: (cargoId: number, row: DACostItem) => {
				if (!cargoId || !cargos || row.type !== DACostItemTypes.LINER_COST) {
					return '-';
				}

				const relevantCargo = cargos.find((c) => c.id === cargoId);

				return relevantCargo?.type ?? '-';
			}
		},
		{
			title: 'Unit type',
			dataIndex: 'unitType',
			width: 100,
			editable: (row?: DACostItem) =>
				row?.type === DACostItemTypes.LINER_COST && Boolean(row.cargoId),
			editingProps: {
				type: 'select' as const,
				options: unitTypeOptions,
				inputProps: {
					'data-testid': 'new-cost-item-unitType'
				},
			},
			render: (_unitType: string, row: DACostItem) => row.unitType ?? '-',
		},
		{
			title: 'Units',
			dataIndex: 'units',
			editable: (row?: DACostItem) => {
				return row?.type !== 'Liner cost';
			},
			editingProps: {
				type: 'number' as const,
				inputProps: {
					'data-testid': 'new-cost-item-unitType'
				},
			},
			width: 100,
			addToFormOverride: true,
			render: (units: number, row: DACostItem) => {
				let unitsToUse = units;

				if (row?.type === DACostItemTypes.LINER_COST && row.cargoId) {
					if (row.unitType === LinerCostUnits.LS) {
						unitsToUse = 1;
					} else {
						const relevantCargo = cargos.find((c) => c.id === row.cargoId);

						if (relevantCargo && selectedPortCall) {
							const cp = relevantCargo.CargoPorts.find(
								(cpo) => cpo.portCallId === selectedPortCall.id,
							);

							if (cp) {
								unitsToUse = cp.blQuantity ?? cp.cpQuantity ?? 0;

								if (
									relevantCargo.secondaryUnit &&
									relevantCargo.secondaryUnit !== relevantCargo.unit &&
									relevantCargo.secondaryUnit === row.unitType
								) {
									unitsToUse =
										cp.secondaryBlQuantity ??
										cp.secondaryCpQuantity ??
										cp.blQuantity ??
										cp.cpQuantity ??
										0;								
								}
							}
						}
					}
				}

				return unitsToUse ? formatNumber(unitsToUse) : '-';
			}
		},
		{
			title: 'Price',
			dataIndex: 'price',
			editable: true,
			editingProps: {
				type: 'number' as const,
				inputProps: {
					'data-testid': 'new-cost-item-price'
				},
			},
			width: 100,
			align: 'right' as const,
			render: (price: number) => currency ? formatCurrency(price ?? 0, currency) : '-'
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			width: 150,
			sorter: (a: DACostItem, b: DACostItem) =>
				getCostItemDisplayAmount(a, { cargos, selectedPortCall }) -
				getCostItemDisplayAmount(b, { cargos, selectedPortCall }),
			sortDirections: (['descend', 'ascend'] as SortOrder[]),
			addToFormOverride: true,
			align: 'right' as const,
			render: (_amount: number, row: DACostItem) => {
				const displayAmount = getCostItemDisplayAmount(row, { cargos, selectedPortCall });

				return formatCurrency(displayAmount, currency ?? Currencies.USD);
			}
		},
	]; 
};

export const getPaymentColumns = ({
	currency
}: {
	currency: Values<typeof Currencies> | undefined
}) => {
	return [
		{
			title: 'Payment Date',
			dataIndex: 'paymentDate',
			editable: true,
			editingProps: {
				type: 'date' as const,
				inputProps: {},
			},
			render: (value: Moment) => value ? toMoment(value).format('YYYY-MM-DD') : '-'
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			editable: true,
			editingProps: {
				type: 'number' as const,
				inputProps: {},
			},
			align: 'right' as const,
			render: (value: number) => currency ? formatCurrency(value ?? 0, currency) : 0,
		},
	];
};
